import React, { useState, useRef } from 'react';
import { View, Text, StyleSheet, ImageBackground, TouchableOpacity, Platform, Animated, Alert, ScrollView, Image, Dimensions, LayoutAnimation } from 'react-native';
import { useRouter, Redirect, useFocusEffect } from 'expo-router';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { Colors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { LOGIN_ENFORCED, VERTICAL_LOGISTICA_VISIBLE } from '../constants/featureFlags';
import { entrarComoInvitado } from '../services/modoInvitado';
import {
  verificacionAplazadaEnEstaSesion,
  aplazarVerificacionEnEstaSesion,
  rutaSegunRol,
} from './_layout';

// Se quitó el import de ScreenBackground: no se usaba en esta pantalla (aquí el
// fondo lo pone el ImageBackground de login-bg.png) y arrastraba al grafo de la
// portada una segunda imagen a pantalla completa —fondo.png, 853×1844— que no se
// pinta nunca. En el arranque cada bitmap de más compite por el mismo montón
// nativo que necesita la superficie del mapa.

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

export default function IndexScreen() {
  const router = useRouter();
  const setRole = useAppStore((state) => state.setRole);
  const setBaseRole = useAppStore((state) => state.setBaseRole);
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [activePromoIndex, setActivePromoIndex] = useState(0);

  const PROMOS = [
    { title: '¡Tu primer viaje es GRATIS!', text: 'Únete hoy y viaja por la casa', icon: 'gift', color: '#E65100' },
    { title: '0% Comisión', text: 'Choferes ganan el 100% de la tarifa', icon: 'cash', color: '#10B981' },
    // El título decía "Seguridad 24/7" y prometía una vigilancia permanente
    // que no existe: no hay nadie mirando viajes ni un centro de monitoreo.
    // Lo que sí se puede señalar en el código es lo que ya decía el texto —
    // el botón SOS (`sos_alerts` en services/firestoreService.ts, atendido
    // desde el panel de admin) y la verificación del chofer, que hace una
    // persona (terms.tsx punto 4) — más el PIN de inicio de viaje.
    { title: 'Seguridad en cada viaje', text: 'Choferes verificados, PIN de viaje y botón SOS', icon: 'shield-checkmark', color: '#208AEF' }
  ];

  // ⚠️ SIN FOCO NO SE ANIMA — EL MISMO CANDADO QUE YA LLEVA EL MAPA.
  //
  // `LayoutAnimation.configureNext` programa una transición en el hilo de UI
  // NATIVO y es GLOBAL: no se limita a esta pantalla. Esta portada NO se
  // desmonta cuando el usuario avanza (index → /auth → /role-selection →
  // /kyc-welcome son push/replace por encima), así que este temporizador seguía
  // armando una animación de layout CADA 4 SEGUNDOS mientras el usuario está en
  // otra pantalla.
  //
  // Si una de esas animaciones se come la transacción de layout con la que se
  // monta otra pantalla, en Fabric las vistas pueden quedar con anchos
  // intermedios y el texto se re-envuelve — que es la pinta que tiene la captura
  // del fundador con la pantalla de vehiculos "en línea".
  //
  // `map.tsx` ya se topó con esto y puso este mismo candado
  // (`if (!isTabFocused) return;`). Aquí faltaba.
  // `useFocusEffect` monta el temporizador al entrar y lo DESMONTA al salir,
  // que es justo lo que hace falta: fuera de foco no queda nada armado.
  useFocusEffect(
    React.useCallback(() => {
      const interval = setInterval(() => {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        setActivePromoIndex((prev) => (prev + 1) % PROMOS.length);
      }, 4000);
      return () => clearInterval(interval);
    }, [])
  );
  
  // Usar Animated nativo para mayor compatibilidad web
  const [translateY] = useState(() => new Animated.Value(SCREEN_HEIGHT)); 

  const toggleModal = () => {
    if (isExpanded) {
      Animated.spring(translateY, {
        toValue: 800,
        useNativeDriver: true,
        friction: 8,
        tension: 40,
      }).start();
    } else {
      Animated.spring(translateY, {
        toValue: 0,
        useNativeDriver: true,
        friction: 8,
        tension: 40,
      }).start();
    }
    setIsExpanded(!isExpanded);
  };

  // FIX HALLAZGO 1 (auditoría de seguridad pre-producción):
  // Cualquier elección de rol que no tenga sesión Firebase activa DEBE pasar
  // por /auth antes de tocar cualquier dashboard. El rol queda guardado en
  // Zustand (setRole/setBaseRole) y el _layout, tras detectar el login,
  // consulta /users/{uid} y enruta al destino correcto (KYC o dashboard).
  // Sin esto, un usuario invitado podía entrar al panel de SCHOOL_DIRECTOR,
  // RESTAURANT_OWNER, FOOD_DELIVERY o al mapa de PASSENGER sin registrarse.
  const handleSelectRole = (role: any) => {
    // VISTA PREVIA: entra directo al panel del rol, sin cuenta y sin KYC.
    // `entrarComoInvitado` fija el rol REAL (no 'GUEST'), enciende la
    // bandera y navega. Las acciones quedan bloqueadas por
    // `bloquearSiInvitado` en cada pantalla; la navegación queda libre.
    if (modoVistaPrevia) {
      entrarComoInvitado(role);
      return;
    }

    setRole(role);
    setBaseRole(role);
    // Entrar por la vía normal apaga cualquier vista previa anterior.
    useAppStore.getState().setGuestPreview(false);

    const hasSession = !!useAppStore.getState().userFirebaseUid;
    if (!hasSession) {
      // Forzar registro/login. El _layout aplicará el guard KYC post-login
      // y decidirá a qué pantalla enviar según el perfil de Firestore.
      router.replace('/auth');
      return;
    }

    // FIX PROFESIONALISMO — Antes para PASSENGER/FOOD_DELIVERY/CAR_RENTER/
    // RESTAURANT_OWNER mandábamos directo al dashboard del rol asumiendo que
    // el _layout iba a reaccionar y rebotar al KYC. PERO `_layout` solo
    // dispara el guard en POST_LOGIN_ENTRY_ROUTES (/, /auth, /role-selection,
    // etc.) — `/(tabs)/map` no está en esa lista, así que el bote nunca
    // ocurría: el usuario sin KYC aterrizaba en el mapa, intentaba pedir
    // viaje y veía "permission denied" (las Firestore rules bloquean rides
    // sin isKycVerified). UX rota.
    //
    // Ahora: si la sesión existe pero la cuenta NO está verificada todavía,
    // forzamos el embudo correspondiente al rol — igual que role-selection.tsx.
    const isVerified = useAppStore.getState().isVerified;
    if (!isVerified) {
      if (role === 'SCHOOL_DIRECTOR') {
        // El alta de la directora ya no existe (perfil escolar anulado el
        // 5-oct-2026). Quien tenga ese rol guardado entra como cualquier otro
        // usuario sin verificar: al KYC normal.
        router.replace('/kyc-welcome');
        return;
      }
      // RENT_CAR_OWNER no requiere KYC personal para entrar al dashboard
      // (gestiona flota y CRM, no maneja vehículo). Las reglas Firestore
      // exigen kycCompleted al CREAR un /vehicle, así que la barrera está
      // server-side cuando intente publicar — el dashboard se navega libre.
      if (role === 'RENT_CAR_OWNER') {
        router.replace('/(tabs)/dashboard');
        return;
      }
      // ══ EL EMBUDO SE ENSEÑA UNA VEZ POR SESIÓN, NO EN CADA TOQUE ═══════
      //
      // Aquí estaba el bucle que el fundador reportó el 17-oct-2026: «entra
      // con el correo, se queda anclado en la pantalla de verificación, y al
      // intentar salir aparece la ventana fea de elegir perfil».
      //
      // `aplazarVerificacionEnEstaSesion()` ya se encendía desde los dos
      // botones de salida del embudo («Ahora no, entrar a la app» y «Cambiar
      // de rol»), y `_layout.tsx:876` ya la respetaba. ESTA pantalla no la
      // miraba. Así que el camino era:
      //
      //   tocar rol → embudo → «Ahora no» → su pantalla → «Cambiar de rol»
      //   → selector → «Volver» → PORTADA → tocar rol → embudo otra vez…
      //
      // Sin salida y sin ningún error que lo explicara.
      //
      // LO QUE ESTO **NO** ABRE: el chofer sigue sin poder ponerse en línea ni
      // aceptar viajes sin verificarse. Lo exigen las reglas de Firestore
      // (`isDriverVerified == true`) y el candado de map.tsx. Eso vive en el
      // SERVIDOR y no se toca desde aquí. El pasajero, por decisión del
      // fundador (3-oct-2026), tiene 5 viajes de cortesía antes del muro, y
      // ese tope lo lleva el servidor, no esta pantalla.
      if (verificacionAplazadaEnEstaSesion()) {
        router.replace(rutaSegunRol(role) as any);
        return;
      }
      // Primera vez en esta sesión: se le enseña el embudo, y se marca ANTES
      // de navegar por el mismo motivo que `_layout.tsx:877` — si se marcara
      // después, salir del embudo pasa por una ruta de entrada, el guardián
      // vuelve a correr y lo devuelve aquí.
      aplazarVerificacionEnEstaSesion();
      router.replace('/kyc-welcome');
      return;
    }

    // Con KYC OK, landing del rol elegido.
    if (role === 'DRIVER' || role === 'SCHOOL_DRIVER' || role === 'DELIVERY_DRIVER') {
      // ══════════════════════════════════════════════════════════════════
      // EL CHOFER APROBADO NO VUELVE A PASAR POR EL ALTA. CADA ARRANQUE.
      // ══════════════════════════════════════════════════════════════════
      // Esto mandaba a TODO chofer a /kyc-welcome —el embudo del registro—
      // en cada arranque en frío, con `replace`, que además borra el
      // historial. El comentario de antes lo decía y lo daba por bueno:
      // «el _layout luego chequea isDriverVerified y decide».
      //
      // Pero ese rebote no siempre ocurre, y cuando no ocurre el chofer se
      // queda DENTRO del embudo sin vuelta atrás: las dos únicas salidas de
      // /kyc-welcome eran borrarle el rol (y acabar en la pantalla que pide
      // elegir cómo usar la app, como si se registrara de nuevo) o cerrar
      // sesión. Es el fallo que el fundador reportó el 1-oct-2026: «la
      // pantalla KYC lo retiene y al intentar salir rompe el menú».
      //
      // No hay motivo para el desvío: aquí ya se sabe. `isVerified` es la
      // identidad (comprobada arriba) e `isDriverVerified` es la habilitación
      // para conducir, que escribe el SERVIDOR al aprobar los papeles
      // (functions/src/kyc.ts). Con las dos, al mapa. Sin la segunda, al
      // embudo — que entonces sí es su sitio, porque le faltan los papeles
      // del vehículo.
      // El aplazamiento vale tambien aqui. Sin esto quedaba una inversion
      // absurda: el chofer MAS avanzado —identidad aprobada, le faltan solo los
      // papeles del vehiculo— daba vueltas en el embudo, mientras el que no
      // tiene nada verificado entraba al mapa. Conducir le sigue cerrado hasta
      // que entregue los papeles: eso lo guarda el servidor, no esta pantalla.
      const habilitadoParaConducir = useAppStore.getState().isDriverVerified;
      if (habilitadoParaConducir || verificacionAplazadaEnEstaSesion()) {
        router.replace('/(tabs)/map');
      } else {
        aplazarVerificacionEnEstaSesion();
        router.replace('/kyc-welcome');
      }
    } else if (role === 'RENT_CAR_OWNER') {
      router.replace('/(tabs)/dashboard');
    } else if (role === 'CAR_RENTER') {
      router.replace('/(tabs)/marketplace');
    } else if (role === 'SCHOOL_DIRECTOR') {
      router.replace('/school-director-dashboard');
    } else if (role === 'FOOD_DELIVERY') {
      router.replace('/(tabs)/food');
    } else if (role === 'RESTAURANT_OWNER') {
      router.replace('/restaurant-dashboard');
    } else {
      router.replace('/(tabs)/map');
    }
  };

  // ACCESO PARA REVISIÓN DE GOOGLE/APPLE (item bloqueador de la auditoría).
  // Durante la ventana de gracia (LOGIN_ENFORCED=false) dejamos ENTRAR como
  // INVITADO para explorar la app sin cuenta — requisito de la política de
  // review de las tiendas (si no pueden entrar, rechazan el submission). El
  // fix de la prórroga en _layout.tsx no bastaba porque handleSelectRole aquí
  // fuerza /auth; este botón (solo visible en la ventana) da la puerta demo.
  // Los guards GUEST en map/wallet/cart empujan a /auth al intentar cualquier
  // acción real, así que NO se expone dinero ni datos. El día
  // LOGIN_ENFORCED_FROM el botón desaparece y el login vuelve obligatorio.
  // MODO VISTA PREVIA — el interruptor de arriba del selector.
  //
  // Antes había un botón "Explorar como invitado" enterrado al final de un
  // scroll de 7 tarjetas, y solo daba el rol PASAJERO. Dos problemas: el
  // revisor de Google tenía que descubrirlo haciendo scroll, y aunque lo
  // encontrara solo veía una séptima parte de la app.
  //
  // Ahora es un interruptor visible arriba: al encenderlo, las MISMAS 7
  // tarjetas de rol entran en modo vista previa. No se duplica nada — el
  // interruptor solo cambia qué hace cada tarjeta.
  const [modoVistaPrevia, setModoVistaPrevia] = useState(false);

  // Si estamos en la Web (build estático), el portal es exclusivamente para Rent Car.
  // Redirigimos a /partner — la creación de cuentas y KYC ocurren solo en la app móvil.
  // IMPORTANTE: el SSR del export web no tiene `window`, así que si no redirigíamos
  // ahí, el cliente veía el selector de roles flashear antes del hydrate. El nuevo
  // criterio es: "es web Y no estamos en localhost" → redirige (también durante SSR).
  if (Platform.OS === 'web') {
    const isLocalhost =
      typeof window !== 'undefined' &&
      window.location.hostname.includes('localhost');
    if (!isLocalhost) {
      return <Redirect href="/partner" />;
    }
  }

  return (
    <ImageBackground 
      source={require('../../assets/images/login-bg.png')} 
      style={styles.background}
      resizeMode="cover"
    >
      <View style={styles.overlay}>
        
        {/* Botón Flotante Inicial para mostrar el menú */}
        {!isExpanded && (
          <TouchableOpacity style={styles.startButton} onPress={toggleModal} activeOpacity={0.8}>
            <Text style={styles.startButtonText}>Comenzar</Text>
            <Ionicons name="chevron-up" size={24} color={Colors.white} />
          </TouchableOpacity>
        )}

        {/* Modal Animado */}
        <Animated.View style={[styles.bottomSheet, { transform: [{ translateY }] }]}>
          
          {/* Botón para cerrar el modal deslizando hacia abajo */}
          <TouchableOpacity style={styles.closeHandle} onPress={toggleModal}>
            <View style={styles.handleBar} />
          </TouchableOpacity>
          
          <View style={styles.stepContainer}>
              <View style={styles.promoContainer}>
                <View style={[styles.promoIconBg, { backgroundColor: PROMOS[activePromoIndex].color }]}>
                  <Ionicons name={PROMOS[activePromoIndex].icon as any} size={24} color="#FFF" />
                </View>
                <View style={styles.promoTextContainer}>
                  <Text style={[styles.promoTitle, { color: PROMOS[activePromoIndex].color }]}>
                    {PROMOS[activePromoIndex].title}
                  </Text>
                  <Text style={styles.promoText}>{PROMOS[activePromoIndex].text}</Text>
                </View>
              </View>

              <Text style={styles.modalTitle}>¿Cómo quieres usar la app hoy?</Text>
              <Text style={styles.modalSubtitle}>Puedes cambiar de modo en cualquier momento desde tu perfil.</Text>

              {/* ── INTERRUPTOR DE VISTA PREVIA ─────────────────────────────
                  Va ARRIBA del selector a propósito: si queda al final de un
                  scroll de 7 tarjetas, quien no sabe que existe nunca lo
                  encuentra — y ese "quien" incluye al revisor de Google.
                  Encendido, las mismas 7 tarjetas de abajo entran sin cuenta. */}
              {!LOGIN_ENFORCED && (
                <TouchableOpacity
                  style={[styles.previewToggle, modoVistaPrevia && styles.previewToggleOn]}
                  onPress={() => setModoVistaPrevia((v) => !v)}
                  activeOpacity={0.8}
                  accessibilityRole="switch"
                  accessibilityState={{ checked: modoVistaPrevia }}
                  accessibilityLabel="Explorar sin crear cuenta"
                >
                  <Ionicons
                    name={modoVistaPrevia ? 'eye' : 'eye-outline'}
                    size={20}
                    color={modoVistaPrevia ? '#FFF' : Colors.accent}
                  />
                  <View style={{ flex: 1, marginLeft: 10 }}>
                    <Text style={[styles.previewToggleTitle, modoVistaPrevia && { color: '#FFF' }]}>
                      {modoVistaPrevia ? 'Explorando sin cuenta' : 'Explorar sin crear cuenta'}
                    </Text>
                    <Text style={[styles.previewToggleSub, modoVistaPrevia && { color: 'rgba(255,255,255,0.85)' }]}>
                      {modoVistaPrevia
                        ? 'Elige abajo el perfil que quieres conocer'
                        : 'Mira la app completa antes de registrarte'}
                    </Text>
                  </View>
                  <View style={[styles.previewSwitch, modoVistaPrevia && styles.previewSwitchOn]}>
                    <View style={[styles.previewKnob, modoVistaPrevia && styles.previewKnobOn]} />
                  </View>
                </TouchableOpacity>
              )}

              <ScrollView
                style={styles.rolesScroll}
                contentContainerStyle={{ paddingBottom: 20 }}
                showsVerticalScrollIndicator={false}
              >
                {/* Opcion Pasajero */}
                <TouchableOpacity 
                  style={[styles.roleCard, { backgroundColor: '#F0F5FA' }]} 
                  onPress={() => handleSelectRole('PASSENGER')}
                  activeOpacity={0.8}
                >
                  <View style={[styles.roleIconContainer, { backgroundColor: '#E3F2FD' }]}>
                    <Image source={require('../../assets/images/quiero viajar.png')} style={styles.customIcon} resizeMode="contain" />
                  </View>
                  <View style={styles.roleTextContainer}>
                    <Text style={[styles.roleTitle, { color: Colors.primary }]}>Quiero Viajar</Text>
                    <Text style={styles.roleDescription}>Pide un taxi o motoconcho. Negocia tu tarifa.</Text>
                  </View>
                </TouchableOpacity>

                {/* Opcion Conductor — taxi, motorista, minibús y repartidor
                    (y chofer escolar solo si el vertical está encendido).
                    El tipo específico se elige en el siguiente paso (kyc-welcome). */}
                <TouchableOpacity
                  style={[styles.roleCard, { backgroundColor: '#EBF8F2' }]}
                  onPress={() => handleSelectRole('DRIVER')}
                  activeOpacity={0.8}
                >
                  <View style={[styles.roleIconContainer, { backgroundColor: '#E8F5E9' }]}>
                    <Image source={require('../../assets/images/quiero conducir.png')} style={styles.customIcon} resizeMode="contain" />
                  </View>
                  <View style={styles.roleTextContainer}>
                    <Text style={[styles.roleTitle, { color: '#10B981' }]}>Conductor</Text>
                    {/* Ofrecía "transporte escolar" con el vertical apagado:
                        VERTICAL_ESCOLAR_VISIBLE = false (featureFlags.ts), la
                        tarjeta de bus escolar de kyc-welcome.tsx está oculta y
                        privacy.tsx punto 10 dice "Transporte escolar (no
                        disponible en esta versión)". El texto sigue a la
                        bandera, igual que en role-selection.tsx. */}
                    <Text style={styles.roleDescription}>
                      Taxi, motor, minibús o delivery. 0% de comisión.
                    </Text>
                  </View>
                </TouchableOpacity>

                {/* Opcion Dueño Rent Car */}
                <TouchableOpacity 
                  style={[styles.roleCard, { backgroundColor: '#Eef2fb' }]} 
                  onPress={() => handleSelectRole('RENT_CAR_OWNER')}
                  activeOpacity={0.8}
                >
                  <View style={[styles.roleIconContainer, { backgroundColor: '#ECEFF1' }]}>
                    <Image source={require('../../assets/images/mi rent car.png')} style={styles.customIcon} resizeMode="contain" />
                  </View>
                  <View style={styles.roleTextContainer}>
                    <Text style={[styles.roleTitle, { color: '#0A2540' }]}>Mi Rent Car (Dueño)</Text>
                    <Text style={styles.roleDescription}>Gestiona tu flota automatizada.</Text>
                  </View>
                </TouchableOpacity>

                {/* Opcion Busco Vehiculo */}
                <TouchableOpacity 
                  style={[styles.roleCard, { backgroundColor: '#FFF5EB' }]} 
                  onPress={() => handleSelectRole('CAR_RENTER')}
                  activeOpacity={0.8}
                >
                  <View style={[styles.roleIconContainer, { backgroundColor: '#FFF3E0' }]}>
                    <Image source={require('../../assets/images/carro.png')} style={styles.customIcon} resizeMode="contain" />
                  </View>
                  <View style={styles.roleTextContainer}>
                    <Text style={[styles.roleTitle, { color: '#F59E0B' }]}>Busco Vehículo</Text>
                    <Text style={styles.roleDescription}>Encuentra un vehículo para trabajar.</Text>
                  </View>
                </TouchableOpacity>

                {/* ══ AQUÍ ESTABA «SOY DIRECTOR(A) DE ESCUELA» ════════════
                    Retirada el 5-oct-2026 por decisión del fundador: «vamos a
                    anular el perfil escolar pero dejando el perfil del
                    conductor». Llevaba meses oculta tras la bandera porque el
                    módulo no funcionaba de punta a punta; ahora se quita del
                    todo y su hueco lo toma Logística.
                    El rol SCHOOL_DIRECTOR NO se borra del código: quien ya lo
                    tenga guardado en Firestore tiene que seguir pudiendo usar
                    la app. `rutaSegunRol` (src/app/_layout.tsx) lo aterriza en
                    el mapa como pasajero, y su tarjeta de vuelta sigue en el
                    perfil. Borrar el rol los dejaría encerrados. */}

                {/* ══ TRANSPORTE DE CARGA ═════════════════════════════════
                    Ocupa el hueco que dejó la directora de escuela.

                    ⚠ Y NO llama a `handleSelectRole`, a diferencia de las otras
                    seis tarjetas. Ese camino FIJA EL ROL en el store ANTES de
                    comprobar si hay sesión (línea 105 antes de la 110), así que
                    cualquiera que toque una tarjeta y abandone queda marcado con
                    ese rol — la Cloud Function `cambiarMiModo` tiene un
                    comentario entero sobre el lío que eso creó con los negocios.
                    Logística es una CAPACIDAD, no un rol: aquí solo se navega. */}
                {VERTICAL_LOGISTICA_VISIBLE && (
                  <TouchableOpacity
                    style={[styles.roleCard, { backgroundColor: '#FFF3E0' }]}
                    onPress={() => {
                      const uid = useAppStore.getState().userFirebaseUid;
                      router.push((uid ? '/logistica' : '/auth') as any);
                    }}
                    activeOpacity={0.8}
                  >
                    <View style={[styles.roleIconContainer, { backgroundColor: '#FFE0B2' }]}>
                      <Ionicons name="cube" size={30} color="#EF6C00" />
                    </View>
                    <View style={styles.roleTextContainer}>
                      <Text style={[styles.roleTitle, { color: '#EF6C00' }]}>Tengo un Vehículo de Carga</Text>
                      <Text style={styles.roleDescription}>Curier, mudanza, carga y autobús para gira. Sin mensualidad: pagas solo por servicio hecho.</Text>
                    </View>
                  </TouchableOpacity>
                )}

                {/* Opcion Pedir Comida */}
                <TouchableOpacity 
                  style={[styles.roleCard, { backgroundColor: '#FFF0F0' }]} 
                  onPress={() => handleSelectRole('FOOD_DELIVERY')}
                  activeOpacity={0.8}
                >
                  <View style={[styles.roleIconContainer, { backgroundColor: '#FFEBEB' }]}>
                    <Image source={require('../../assets/images/comida.png')} style={styles.customIcon} resizeMode="contain" />
                  </View>
                  <View style={styles.roleTextContainer}>
                    <Text style={[styles.roleTitle, { color: '#FF3B30' }]}>Pedir a Domicilio</Text>
                    {/* Decía «Tus restaurantes favoritos a domicilio» cuando el
                        catálogo ya tiene farmacia y supermercado: quien busca
                        una medicina no entraba por aquí. */}
                    <Text style={styles.roleDescription}>Comida, farmacia, supermercado. Lo que necesites, en tu puerta.</Text>
                  </View>
                </TouchableOpacity>

                {/* ══ LA PUERTA DE TODOS LOS NEGOCIOS, NO SOLO DE LOS RESTAURANTES ══
                    Decía «Tengo un Restaurante» y «Sube tu menú». Detrás de esa
                    tarjeta vive el registro de CUALQUIER negocio: el selector
                    de categorías ya tiene farmacia y supermercado, el reparto
                    no mira la categoría para nada y las reglas tampoco. Pero
                    el dueño de una farmacia miraba esta pantalla, no se veía en
                    ninguna de las siete tarjetas, y se iba. La única puerta
                    que tenía estaba rotulada con el nombre de otro gremio. */}
                <TouchableOpacity 
                  style={[styles.roleCard, { backgroundColor: '#FFF8E1' }]} 
                  onPress={() => handleSelectRole('RESTAURANT_OWNER')}
                  activeOpacity={0.8}
                >
                  <View style={[styles.roleIconContainer, { backgroundColor: '#FFECB3' }]}>
                    <Image source={require('../../assets/images/restaurant.png')} style={styles.customIcon} resizeMode="contain" />
                  </View>
                  <View style={styles.roleTextContainer}>
                    <Text style={[styles.roleTitle, { color: '#FF8F00' }]}>Tengo un Negocio</Text>
                    {/* ⚠️ «TE LLEVAS EL 100%» NO ERA FALSO, PERO SE LEÍA MAL — Y LO
                        ESCRIBÍ YO EN ESTA MISMA SESIÓN.

                        Es verdad que Go Safely no se queda con nada de la venta:
                        `application_fee_amount: 0` (functions/src/orders.ts) y no
                        hay ninguna comisión por pedido en todo el servidor. Pero
                        puesto justo después de «nosotros ponemos el mensajero»,
                        la frase se lee como «esto no te cuesta nada», y sí le
                        cuesta: el mensajero lo paga ÉL.

                        Aclaración del fundador (3-oct-2026): «el restaurante debe
                        pagarle al delivery por el envío, ya que el mensajero no
                        está pagando a nosotros su membresía; dejamos ese negocio
                        sin cobrarle por motivo de que se quejan del alto costo de
                        las otras apps».

                        O sea que lo que de verdad se vende —y es un argumento
                        mejor— es: nosotros no cobramos comisión, tú te arreglas
                        con el mensajero. Eso es lo que dice ahora. */}
                    <Text style={styles.roleDescription}>Restaurante, farmacia, supermercado o cualquier otro. Sube tus productos y no te cobramos ni comisión ni cuota: el envío lo arreglas tú con el mensajero.</Text>
                  </View>
                </TouchableOpacity>

                {/* Opcion Ya Soy Usuario */}
                <View style={{ height: 1, backgroundColor: Colors.border, marginVertical: 10, marginHorizontal: 20 }} />
                
                <TouchableOpacity 
                  style={[styles.roleCard, { backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: Colors.primary }]} 
                  onPress={() => router.push({ pathname: '/auth', params: { isLogin: 'true' } })}
                  activeOpacity={0.8}
                >
                  <View style={[styles.roleIconContainer, { backgroundColor: '#F0F0F0' }]}>
                    <Ionicons name="log-in" size={28} color={Colors.primary} />
                  </View>
                  <View style={styles.roleTextContainer}>
                    <Text style={[styles.roleTitle, { color: Colors.primary }]}>Ya tengo una cuenta</Text>
                    <Text style={styles.roleDescription}>Inicia sesión para continuar.</Text>
                  </View>
                </TouchableOpacity>

                {/* El botón "Explorar como invitado" que estaba aquí se
                    eliminó: quedaba al final de un scroll de 7 tarjetas — casi
                    nadie llegaba — y además solo daba el rol PASAJERO.
                    Lo reemplaza el interruptor de vista previa que ahora está
                    ARRIBA del selector, y que habilita los 7 roles. */}

              </ScrollView>
            </View>

        </Animated.View>
      </View>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  background: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  startButton: {
    position: 'absolute',
    bottom: Platform.OS === 'ios' ? 40 : 32,
    alignSelf: 'center',
    backgroundColor: Colors.primary,
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 30,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 10,
    elevation: 8,
    zIndex: 10,
  },
  startButtonText: {
    color: Colors.white,
    fontSize: 18,
    fontWeight: 'bold',
    marginRight: 8,
  },
  bottomSheet: {
    position: 'absolute',
    bottom: Platform.OS === 'ios' ? 40 : 20,
    backgroundColor: 'rgba(255, 255, 255, 0.85)', // Efecto Glassmorphism
    borderRadius: 24,
    paddingHorizontal: 24,
    paddingTop: 12,
    paddingBottom: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 10,
    width: '92%',
    maxWidth: 500,
    maxHeight: '85%',
    alignSelf: 'center',
  },
  closeHandle: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: 12,
    marginBottom: 4,
  },
  handleBar: {
    width: 40,
    height: 5,
    borderRadius: 3,
    backgroundColor: '#D0D0D0',
  },
  stepContainer: {
    width: '100%',
    flexShrink: 1,
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: Colors.text,
    textAlign: 'center',
    marginBottom: 8,
  },
  previewToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: Colors.accent,
    backgroundColor: 'rgba(255,255,255,0.9)',
    marginBottom: 16,
  },
  previewToggleOn: {
    backgroundColor: Colors.accent,
    borderColor: Colors.accent,
  },
  previewToggleTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: Colors.accent,
  },
  previewToggleSub: {
    fontSize: 12,
    color: Colors.textSecondary,
    marginTop: 1,
  },
  previewSwitch: {
    width: 42,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#D0D5DD',
    padding: 3,
    justifyContent: 'center',
  },
  previewSwitchOn: {
    backgroundColor: 'rgba(255,255,255,0.35)',
  },
  previewKnob: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#FFF',
  },
  previewKnobOn: {
    alignSelf: 'flex-end',
  },
  modalSubtitle: {
    fontSize: 14,
    color: Colors.textSecondary,
    textAlign: 'center',
    marginBottom: 20,
  },
  rolesScroll: {
    width: '100%',
  },
  roleCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 16,
    marginBottom: 12,
  },
  roleIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    overflow: 'hidden',
  },
  // Estos 44 dp son el TOPE al que se dibujan los PNG de las tarjetas de rol.
  // Los archivos venían a 1254×1254 y React Native los decodifica a tamaño
  // completo: `ReactImageView` solo pide un submuestreo para `content://` y
  // `file://`, y estos van empaquetados como `res://`. Eran 6 MB de montón
  // NATIVO por icono — 36 MB solo por abrir la portada, invisibles para
  // Crashlytics porque al desbordar el montón nativo Android mata el proceso
  // sin excepción ni traza. Ahora los archivos están a 176 px (44 dp × 4, la
  // densidad máxima real en Android), que es todo lo que esta caja puede
  // enseñar. Si alguien vuelve a subir el arte a resolución de imprenta, el
  // teléfono barato se vuelve a cerrar solo: los originales están fuera del
  // proyecto, en «Go safely/assets-originales-full-res».
  customIcon: {
    width: 44,
    height: 44,
  },
  roleTextContainer: {
    flex: 1,
  },
  roleTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  roleDescription: {
    fontSize: 16,
    color: '#000',
    opacity: 0.7,
  },
  promoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 16,
    padding: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  promoIconBg: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  promoTextContainer: {
    flex: 1,
  },
  promoTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  promoText: {
    fontSize: 13,
    color: '#64748B',
  },
  loginHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    width: '100%',
  },
  backButton: {
    padding: 4,
  },
  loginTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.text,
  },
  primaryButton: {
    backgroundColor: Colors.primary,
    width: '100%',
    height: 52,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  primaryButtonText: {
    color: Colors.white,
    fontSize: 16,
    fontWeight: 'bold',
  },
  secondaryButton: {
    backgroundColor: Colors.white,
    width: '100%',
    height: 52,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: Colors.accent,
    marginBottom: 20,
  },
  secondaryButtonText: {
    color: Colors.accent,
    fontSize: 16,
    fontWeight: 'bold',
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    marginBottom: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#E0E0E0',
  },
  dividerText: {
    marginHorizontal: 16,
    color: Colors.textSecondary,
    fontSize: 13,
    fontWeight: '600',
  },
  socialContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 20,
    marginBottom: 20,
  },
  socialButton: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: Colors.white,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#F0F0F0',
  },
  forgotPasswordButton: {
    marginBottom: 20,
    paddingVertical: 4,
    alignSelf: 'center',
  },
  forgotPasswordText: {
    color: Colors.textSecondary,
    fontSize: 14,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  guestButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 4,
  },
  guestButtonText: {
    color: Colors.accent,
    fontSize: 16,
    fontWeight: 'bold',
  },
});
